import { ToolOutlined } from "@ant-design/icons";
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath } from "@xyflow/react";
import { EdgeContextMenu } from "components/CC/UI/EdgeContextMenu";
import { Fragment, useState } from "react";

const EDGE_HIGHLIGHT_COLOR = "#06b6d4"; // tailwind cyan-500 — matches wrench hover color

interface CustomEdgeData {
    onWrenchClick?: (edgeId: string) => void;
    onDelete?: (edgeId: string) => void;
    showWrench?: boolean;
    isHovered?: boolean;
    // Condition form data
    active?: boolean;
    title?: string;
    subtitle?: string;
    result?: string;
    conditions?: Array<{
        source: string;
        value: string;
        operator: string;
        output: string;
    }>;
    // Index signature for compatibility with Record<string, unknown>
    [key: string]: unknown;
}

interface CustomEdgeProps extends EdgeProps {
    data?: CustomEdgeData;
}

export const CustomEdge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
    data,
}: CustomEdgeProps) => {
    const [edgePath, centerX, centerY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });

    // Place wrench at ~33% from source by shifting 30% from center toward source,
    // but clamp so it's never closer than MIN_DIST pixels from the source node
    const MIN_DIST = 40;
    let labelX = centerX + (sourceX - centerX) * 0.3;
    let labelY = centerY + (sourceY - centerY) * 0.3;

    const dxFromSource = labelX - sourceX;
    const dyFromSource = labelY - sourceY;
    const distFromSource = Math.hypot(dxFromSource, dyFromSource);
    if (distFromSource < MIN_DIST) {
        const fallbackDx = targetX - sourceX;
        const fallbackDy = targetY - sourceY;
        const fallbackDist = Math.hypot(fallbackDx, fallbackDy);
        const [unitX, unitY] =
            distFromSource > 0
                ? [dxFromSource / distFromSource, dyFromSource / distFromSource]
                : fallbackDist > 0
                  ? [fallbackDx / fallbackDist, fallbackDy / fallbackDist]
                  : sourcePosition === "left"
                    ? [-1, 0]
                    : sourcePosition === "top"
                      ? [0, -1]
                      : sourcePosition === "bottom"
                        ? [0, 1]
                        : [1, 0];
        labelX = sourceX + unitX * MIN_DIST;
        labelY = sourceY + unitY * MIN_DIST;
    }

    const [isWrenchHovered, setIsWrenchHovered] = useState(false);
    const isHighlighted = Boolean(data?.isHovered) || isWrenchHovered;

    const configuredConditionCount =
        data?.conditions?.filter(
            (condition) => Boolean(condition?.operator?.trim()) && Boolean(condition?.value?.trim())
        ).length ?? 0;
    const isConfigured = configuredConditionCount > 0;

    const handleWrenchClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        data?.onWrenchClick?.(id);
    };

    const handleSettings = () => {
        data?.onWrenchClick?.(id);
    };

    const handleDelete = () => {
        data?.onDelete?.(id);
    };

    return (
        <Fragment>
            <BaseEdge
                id={id}
                path={edgePath}
                style={{
                    strokeWidth: isHighlighted ? 2.5 : 2,
                    stroke: isHighlighted ? EDGE_HIGHLIGHT_COLOR : "#0706254D",
                    strokeDasharray: "6 4",
                    transition: "stroke 0.15s, stroke-width 0.15s",
                    ...style,
                }}
                markerEnd={markerEnd}
            />

            <EdgeLabelRenderer>
                {/* Wrench icon - 5px above the line, clickable - with context menu */}
                {data?.showWrench && (
                    <div
                        style={{
                            position: "absolute",
                            transform: `translate(-50%, -100%) translate(${labelX}px,${labelY - 5}px)`,
                            pointerEvents: "all",
                        }}
                        className="nodrag nopan"
                    >
                        <EdgeContextMenu onSettings={handleSettings} onDelete={handleDelete}>
                            <div
                                style={{ cursor: "pointer" }}
                                onClick={handleWrenchClick}
                                onMouseEnter={() => setIsWrenchHovered(true)}
                                onMouseLeave={() => setIsWrenchHovered(false)}
                                className="relative"
                            >
                                <ToolOutlined
                                    style={{ fontSize: 14 }}
                                    className={`${isHighlighted ? "text-cyan-500" : isConfigured ? "text-primary" : "text-gray-400"} transition-colors`}
                                />
                                {isConfigured && (
                                    <span className="absolute -top-2 -right-2 min-w-[13px] h-[13px] rounded-[100%] bg-primary text-white text-[9px] font-bold flex items-center justify-center px-0.5 leading-none">
                                        {configuredConditionCount}
                                    </span>
                                )}
                            </div>
                        </EdgeContextMenu>
                    </div>
                )}
                {/* Label from condition form - title and subtitle, only for HTTP edges */}
                {data?.showWrench && (data?.title || data?.subtitle) && (
                    <div
                        style={{
                            position: "absolute",
                            transform: `translate(-50%, 0%) translate(${labelX}px,${labelY + 5}px)`,
                            pointerEvents: "none",
                        }}
                        className="nodrag nopan text-center"
                    >
                        {data.title && <div className="text-xs font-semibold text-gray-700">{data.title}</div>}
                        {data.subtitle && <div className="text-xs text-gray-500">{data.subtitle}</div>}
                    </div>
                )}
            </EdgeLabelRenderer>
        </Fragment>
    );
};

export type { CustomEdgeData };
